import { v4 as uuid } from "uuid";
import { NodeType } from "./constants";
import { isVegaLiteNodeType } from "./registry/vegaLiteNodes";
import { dataflowOrder } from "./utils/dataflowOrder";
import { unversionedNodeType } from "./utils/flowNodeCanonicalType";
import { inputSlotOf, slotHandleId } from "./utils/inputSlots";
import { VEGA_LITE, inputNamesIn, resolveReferences, widgetLiteral } from "./utils/references/codeReferences";
import { PARAMETER_NODE_TYPE, sharedWidgetsOfSpec } from "./utils/references/sharedParameters";
import { normalizeSelections } from "./utils/references/selectionTags";
import { normalizeWidgets, type WidgetDef, type WidgetValue } from "./utils/widgets/widgetModel";
import { INPUTS_DATASET, namesDataset, readsInputs, usesNamedDatasets } from "./utils/vegaDatasets";
import { inputTableName } from "./contracts/autkGrammar";
import { normalizeScenarios } from "./utils/scenarios/scenarioModel";
import { inputScenarios } from "./utils/scenarios/inputScenarios";
import { NO_SCENARIO_COLOR, type ScenarioLabel } from "./utils/scenarios/scenarioColors";

// ── Trill types ──────────────────────────────────────────────────────────────

interface TrillNode {
  id: string;
  type: string;
  x: number;
  y: number;
  content?: string;
  /** Display header. Absent here until #235 - which is why every imported
   *  cell rendered as an identical "Python Computation". */
  title?: string;
  in?: unknown;
  out?: unknown;
  metadata?: { widgets?: unknown; selections?: unknown; packageTemplateLabel?: unknown };
}

interface TrillEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
  targetHandle?: string;
}

/** The circle an edge feeds: its handle, or the legacy `in_N` suffix of its id. */
function edgeSlot(edge: TrillEdge): number {
  if (edge.targetHandle) return inputSlotOf(edge.targetHandle);
  const legacy = typeof edge.id === "string" ? edge.id.match(/in_(\d+)$/) : null;
  return legacy ? parseInt(legacy[1], 10) : 0;
}

interface TrillDataflow {
  nodes: TrillNode[];
  edges: TrillEdge[];
  name: string;
  task: string;
  timestamp: number;
  provenance_id: string;
  packages?: string[];
  /** The dataflow's scenario boxes (#662), as a save writes them. */
  scenarios?: unknown;
}

export interface TrillSpec {
  dataflow: TrillDataflow;
}

// ── Notebook types ───────────────────────────────────────────────────────────

interface NotebookCell {
  cell_type: "code" | "markdown" | string;
  source: string;
  metadata: Record<string, unknown>;
  outputs: unknown[];
  execution_count: null;
}

export interface Notebook {
  cells: NotebookCell[];
  metadata: Record<string, unknown>;
  nbformat: number;
  nbformat_minor: number;
}

// ── Node type inference ──────────────────────────────────────────────────────

const DATA_LOADING_PATTERN =
  /\bpd\.read_\w+\s*\(|\bgpd\.read_\w+\s*\(|\bgeopandas\.read_\w+\s*\(|\bopen\s*\(|\brequests\.(get|post|put|delete|patch)\s*\(|\bsqlite3\.connect\s*\(|\bpsycopg2\.connect\s*\(|\bcreate_engine\s*\(|\bboto3\b/;

function isVegaLiteJson(text: string): boolean {
  try {
    const parsed = JSON.parse(text.trim()) as Record<string, unknown>;
    if (typeof parsed === "object" && parsed !== null) {
      const schema = parsed["$schema"];
      return typeof schema === "string" && schema.includes("vega-lite");
    }
  } catch {
    // not JSON
  }
  return false;
}

function inferNodeType(code: string): NodeType {
  if (DATA_LOADING_PATTERN.test(code)) return NodeType.DATA_LOADING;
  if (isVegaLiteJson(code)) return NodeType.VIS_VEGA;
  return NodeType.COMPUTATION_ANALYSIS;
}

// ── Import: Notebook → Trill ─────────────────────────────────────────────────

type CellEdge = { source: number; target: number };

/** A line magic (`%matplotlib inline`), a cell magic (`%%time`) or a shell
 *  escape (`!pip install geopandas`): not Python. The analyzer's
 *  `_MAGIC_OR_SHELL_LINE` (`backend/app/notebooks/analyzer.py`). */
const MAGIC_OR_SHELL_LINE = /^\s*[%!]/;

/** *code* with its IPython lines commented out, so the Python around them
 *  runs in a node, which still shows them (#160). */
function commentOutMagics(code: string): string {
  return code
    .split("\n")
    .map((line) => (MAGIC_OR_SHELL_LINE.test(line) ? line.replace(/^(\s*)/, "$1# ") : line))
    .join("\n");
}

function wireCode(
  code: string,
  cellIdx: number,
  lastVars: (string | null)[],
  hasOutgoing: Set<number>,
  incomingSources: Map<number, number[]>,
): string {
  let out = code;
  const sources = incomingSources.get(cellIdx) ?? [];
  if (sources.length === 1) {
    const srcVar = lastVars[sources[0]];
    if (srcVar) out = `${srcVar} = input_0\n${out}`;
  } else if (sources.length > 1) {
    // A node reads the input on circle k as `input_k`, and the edge from the
    // k-th source lands on circle k (#160).
    const bound = sources
      .map((source, k) => (lastVars[source] ? `${lastVars[source]} = input_${k}` : null))
      .filter((line): line is string => line !== null);
    if (bound.length > 0) out = `${bound.join("\n")}\n${out}`;
  }
  const lv = lastVars[cellIdx];
  if (hasOutgoing.has(cellIdx) && lv) {
    out = `${out}\nreturn ${lv}`;
  }
  return out;
}

const COLUMN_STRIDE = 700;
const ROW_STRIDE = 450;

/**
 * How many nodes a level may stack before it starts a new column.
 *
 * Every node with no incoming edge lands on level 0, so a notebook full of
 * independent cells used to produce one unbounded vertical column - twenty
 * cells meant a 9000px drop nobody scrolls through (#235). Wrapping keeps a
 * level roughly screen-shaped. The cap applies to every level uniformly and
 * only engages past it, so an ordinary pipeline lays out exactly as before.
 */
const MAX_ROWS_PER_LEVEL = 6;

function computeLayout(
  count: number,
  edges: CellEdge[],
): Array<{ x: number; y: number }> {
  const level = new Array<number>(count).fill(0);
  for (const { source, target } of edges) {
    if (level[source] + 1 > level[target]) {
      level[target] = level[source] + 1;
    }
  }
  const countPerLevel = new Map<number, number>();
  return level.map((lv) => {
    const pos = countPerLevel.get(lv) ?? 0;
    countPerLevel.set(lv, pos + 1);
    // Overflow spills into sub-columns to the right of the level. The stride
    // is half a column so a wrapped level stays visually part of its own
    // stage rather than reading as the next one.
    const wrap = Math.floor(pos / MAX_ROWS_PER_LEVEL);
    return {
      x: lv * COLUMN_STRIDE + wrap * (COLUMN_STRIDE / 2),
      y: (pos % MAX_ROWS_PER_LEVEL) * ROW_STRIDE,
    };
  });
}

export const SETUP_NODE_TITLE = "Setup / Imports";

/**
 * Fold several import-only cells into one block of source.
 *
 * Lines are deduplicated on their trimmed text and kept in first-seen order,
 * because notebooks routinely repeat `import pandas as pd` in three separate
 * setup cells and the merged node should read like something a person wrote.
 * Blank lines are dropped: the cell boundaries they used to separate no
 * longer exist. IPython lines (`%matplotlib inline`, `!pip install ...`) are
 * commented out: the Setup node runs before every other, and a line that is
 * not Python failed it at once (#160).
 */
export function mergeImportCells(sources: string[]): string {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const source of sources) {
    for (const raw of commentOutMagics(source).split("\n")) {
      const line = raw.trimEnd();
      const key = line.trim();
      if (key === "") continue;
      if (seen.has(key)) continue;
      seen.add(key);
      lines.push(line);
    }
  }
  return lines.join("\n");
}

export async function notebookToTrill(
  notebook: Record<string, unknown>,
  backendUrl: string
): Promise<TrillSpec> {
  const rawCells = Array.isArray(notebook.cells) ? notebook.cells : [];

  const codeCells = rawCells
    .filter((c) => (c as Record<string, unknown>).cell_type === "code")
    .map((c) => {
      const cell = c as Record<string, unknown>;
      const source = cell.source;
      return Array.isArray(source) ? source.join("") : String(source ?? "");
    });

  // Call backend for AST-based dependency analysis + Altair spec extraction
  type CellAnalysis = {
    defined: string[];
    used: string[];
    last_var: string | null;
    altair_spec: Record<string, unknown> | null;
    is_import_only?: boolean;
  };
  let cellEdges: CellEdge[] = [];
  let lastVars: (string | null)[] = [];
  let altairSpecs: (Record<string, unknown> | null)[] = [];
  let importOnly: boolean[] = [];
  // Whether the analyzer actually answered. Distinct from "returned no edges":
  // a notebook of genuinely independent cells has none, and fabricating a
  // chain for it would wire inputs between cells the AST proved unrelated - and
  // stretch the layout 700px per cell for dependencies that do not exist.
  let analyzed = false;
  try {
    const response = await fetch(`${backendUrl}/api/analyzeNotebook`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cells: codeCells }),
    });
    if (response.ok) {
      const data = (await response.json()) as {
        edges: CellEdge[];
        analysis: CellAnalysis[];
      };
      cellEdges = data.edges ?? [];
      lastVars = (data.analysis ?? []).map((a) => a.last_var ?? null);
      altairSpecs = (data.analysis ?? []).map((a) => a.altair_spec ?? null);
      importOnly = (data.analysis ?? []).map((a) => a.is_import_only === true);
      analyzed = Array.isArray(data.analysis);
    }
  } catch {
    console.warn(
      "NotebookConvertor: backend unreachable, falling back to linear chain."
    );
  }

  // Linear fallback, for when the analyzer could not be reached at all. A
  // chain is a guess, but a defensible one with no information; it used to
  // fire whenever the response merely carried no edges, which meant a notebook
  // the analyzer had correctly found to be independent got the guess anyway.
  if (!analyzed && codeCells.length > 1) {
    for (let i = 0; i < codeCells.length - 1; i++) {
      cellEdges.push({ source: i, target: i + 1 });
    }
    // No lastVars available — skip wiring in this path
    lastVars = [];
    // …and no analysis, so nothing can be classified. Merging on a guess would
    // silently move code the user wrote into a node they did not: without the
    // backend, every cell keeps its own node exactly as before.
    importOnly = [];
  }

  // Build wiring sets. A cell's inputs keep the order of its edges: the k-th
  // is read as `input_k` (wireCode) and its edge lands on circle k (#160).
  const hasOutgoing = new Set(cellEdges.map((e) => e.source));
  const incomingSources = new Map<number, number[]>();
  const circleOf: number[] = [];
  for (const { source, target } of cellEdges) {
    if (!incomingSources.has(target)) incomingSources.set(target, []);
    const sources = incomingSources.get(target)!;
    circleOf.push(sources.length);
    sources.push(source);
  }

  // Import-only cells collapse into a single Setup node. They can have no
  // edges by construction (see analyzer._is_import_only), so removing them
  // cannot disconnect anything - which is what makes the remap below safe.
  const mergedCells = codeCells.filter((_, i) => importOnly[i]);
  const keptIndices = codeCells
    .map((_, i) => i)
    .filter((i) => !importOnly[i]);

  // Original cell index -> index among the kept cells. Edges, positions and
  // ids are all in kept-space; wiring stays in original-space, because
  // `lastVars`/`hasOutgoing`/`incomingSources` are indexed by cell.
  const keptSlot = new Map<number, number>();
  keptIndices.forEach((original, slot) => keptSlot.set(original, slot));

  const keptEdges: Array<CellEdge & { circle: number }> = cellEdges
    .map((edge, i) => ({ ...edge, circle: circleOf[i] }))
    .filter(({ source, target }) => keptSlot.has(source) && keptSlot.has(target))
    .map(({ source, target, circle }) => ({
      source: keptSlot.get(source)!,
      target: keptSlot.get(target)!,
      circle,
    }));

  const positions = computeLayout(keptIndices.length, keptEdges);
  const nodeIds = keptIndices.map(() => uuid());

  const nodes: TrillNode[] = keptIndices.map((original, slot) => {
    const code = commentOutMagics(codeCells[original]);
    const spec = altairSpecs[original] ?? null;
    const nodeType = spec ? NodeType.VIS_VEGA : inferNodeType(code);
    const content = spec
      ? JSON.stringify(spec, null, 2)
      : lastVars.length > 0
        ? wireCode(code, original, lastVars, hasOutgoing, incomingSources)
        : code;
    const node: TrillNode = {
      id: nodeIds[slot],
      type: nodeType,
      x: positions[slot].x,
      y: positions[slot].y,
      content,
    };
    // Name the node after what it produces. Without this every node renders
    // as its template label, so a twenty-cell notebook imported as twenty
    // boxes all reading "Python Computation" (#235).
    const producedName = lastVars[original];
    if (producedName) node.title = producedName;
    return node;
  });

  if (mergedCells.length > 0) {
    // Placed up and to the left of the pipeline rather than in column 0, where
    // it would sit on top of the first real stage. Curio hoists a node's
    // imports into a session-scoped namespace, so one Setup node run first
    // genuinely serves the rest of the dataflow - this is not just grouping.
    nodes.unshift({
      id: uuid(),
      type: NodeType.COMPUTATION_ANALYSIS,
      x: -COLUMN_STRIDE / 2,
      y: -ROW_STRIDE / 2,
      title: SETUP_NODE_TITLE,
      content: mergeImportCells(mergedCells),
    });
  }

  // Each edge names its circle: without a handle every edge landed on circle
  // 0, so a cell with several inputs got one of them (#160).
  const edgeList: TrillEdge[] = keptEdges.map(({ source, target, circle }) => ({
    id: uuid(),
    source: nodeIds[source],
    target: nodeIds[target],
    targetHandle: slotHandleId(circle),
  }));

  return {
    dataflow: {
      nodes,
      edges: edgeList,
      name: "Imported Notebook",
      task: "",
      timestamp: Date.now(),
      provenance_id: uuid(),
    },
  };
}

// ── Export: Trill → Notebook ─────────────────────────────────────────────────

function sanitizeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9]/g, "_");
}

function outputVarName(node: TrillNode): string {
  const safe = sanitizeId(node.id);
  // Specs saved since the curio.builtin@1 pack carry versioned ids (dev/64).
  const nodeType = unversionedNodeType(node.type);
  if (nodeType === NodeType.DATA_LOADING) return `data_${safe}`;
  if (isVegaLiteNodeType(nodeType)) return `vega_${safe}`;
  return `result_${safe}`;
}

/** Templates whose ``content`` is a Python function body, run as ``userCode(input_0, ...)``. */
const PYTHON_BODY_TYPES = new Set<string>([
  NodeType.DATA_LOADING,
  NodeType.DATA_TRANSFORMATION,
  NodeType.COMPUTATION_ANALYSIS,
  NodeType.DATA_SUMMARY,
  NodeType.DATA_EXPORT,
]);

/** ``curio.builtin/spatial-join`` has no ``NodeType`` member (the enum predates it). */
const SPATIAL_JOIN_TYPE = "curio.builtin/spatial-join";

function codeCell(source: string): NotebookCell {
  return {
    cell_type: "code",
    source,
    metadata: {},
    outputs: [],
    execution_count: null,
  };
}

function markdownCell(source: string): NotebookCell {
  return {
    cell_type: "markdown",
    source,
    metadata: {},
    outputs: [],
    execution_count: null,
  };
}

/** The circles a node's code reads, as the sandbox binds them: each wired
 * circle k, and each `input_k` the code names, is a parameter `input_k`
 * (`utk_curio/sandbox/util/input_names.py`). */
function inputParams(content: string, slots: number[]): number[] {
  const read = inputNamesIn(content, "python").map((name) => name.slot);
  return Array.from(new Set([...slots, ...read])).sort((a, b) => a - b);
}

function indent(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => (line.trim() === "" ? "" : `    ${line}`))
    .join("\n");
}

/**
 * *value*, JSON data, as a Python literal laid out as `JSON.stringify(value,
 * null, 2)` lays it out: True, False and None where JSON writes true, false
 * and null, and None for a number JSON cannot write. A key or an item JSON
 * leaves out (an undefined one) is left out here too.
 */
function pythonLiteral(value: unknown, margin = ""): string {
  const inner = `${margin}  `;
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    return `[\n${value.map((item) => inner + pythonLiteral(item ?? null, inner)).join(",\n")}\n${margin}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value).filter(([, item]) => item !== undefined);
    if (entries.length === 0) return "{}";
    const lines = entries.map(([key, item]) => `${inner}${JSON.stringify(key)}: ${pythonLiteral(item, inner)}`);
    return `{\n${lines.join(",\n")}\n${margin}}`;
  }
  return widgetLiteral(value as WidgetValue, "python");
}

/** A heading a reader can use to line the notebook up against the canvas. */
function nodeHeading(node: TrillNode, inputNodes: TrillNode[]): string {
  const inputs = inputNodes.length
    ? ` &larr; ${inputNodes.map(outputVarName).join(", ")}`
    : "";
  return `### \`${unversionedNodeType(node.type)}\` &mdash; \`${node.id}\`${inputs}`;
}

/** Emit the cells for one node.
 *
 * Returns a list rather than a single cell (or ``null``) so that a node can
 * contribute a heading plus a body, and so that a template a Python kernel
 * cannot run still leaves a record instead of vanishing. The previous version
 * returned ``null`` for every type except three, and ``trillToNotebook`` then
 * dropped those nodes without saying so.
 */
function generateCells(
  node: TrillNode,
  inputNodes: TrillNode[],
  inputSlots: number[] = [],
  shared: WidgetDef[] = [],
  inputLabels: ReadonlyMap<number, ScenarioLabel> = new Map(),
): NotebookCell[] {
  // Specs saved since the curio.builtin@1 pack carry versioned ids (dev/64),
  // and third-party package ids never match a NodeType at all - so this
  // dispatch always ends in a default branch rather than an enumeration.
  const nodeType = unversionedNodeType(node.type);
  // A package's chart that draws with the Vega-Lite grammar is a Vega-Lite
  // chart here too (registry/vegaLiteNodes).
  const vegaChart = isVegaLiteNodeType(nodeType);
  // The code the canvas runs: its widget, input, column, shared, selection and
  // scenario references resolved, as the browser does before posting it (#662),
  // each input labelled by the box its node is in.
  const language = vegaChart || nodeType === NodeType.AUTK_GRAMMAR
    ? "json"
    : nodeType === NodeType.JS_COMPUTATION ? "javascript" : "python";
  const content = resolveReferences(
    node.content ?? "",
    {
      widgets: normalizeWidgets(node.metadata?.widgets),
      inputs: inputSlots.map((slot) => {
        const scenario = inputLabels.get(slot);
        return scenario ? { slot, scenario } : { slot };
      }),
      shared,
      selections: normalizeSelections(node.metadata?.selections),
    },
    language,
    vegaChart ? VEGA_LITE : undefined,
  ).code;
  const outVar = outputVarName(node);
  const heading = markdownCell(nodeHeading(node, inputNodes));

  if (PYTHON_BODY_TYPES.has(nodeType)) {
    // A node's content is the body of a function whose parameters are its
    // inputs, `def userCode(input_0=None, input_1=None, ...)`: PythonInterpreter
    // indents it by four spaces and the sandbox execs it under that signature.
    // Reproducing the function is the only faithful inversion: emitting the
    // body flat would leave a top-level `return` (a SyntaxError) and unbound
    // inputs. Each upstream's value is passed as its circle's input.
    const fn = `node_${sanitizeId(node.id)}`;
    const body = content.trim() === "" ? "    pass" : indent(content);
    const slots = inputSlots.length === inputNodes.length ? inputSlots : inputNodes.map((_, i) => i);
    const params = inputParams(content, slots).map((k) => `input_${k}=None`).join(", ");
    const args = inputNodes.map((n, i) => `input_${slots[i]}=${outputVarName(n)}`).join(", ");
    return [
      heading,
      codeCell(`def ${fn}(${params}):\n${body}\n\n\n${outVar} = ${fn}(${args})`),
    ];
  }

  if (nodeType === NodeType.DATA_POOL) {
    // A pool passes its input through untouched.
    const source = inputNodes.length
      ? `${outVar} = ${outputVarName(inputNodes[0])}`
      : `${outVar} = None`;
    return [heading, codeCell(source)];
  }

  if (vegaChart) {
    let spec: unknown = {};
    let parsed = true;
    try {
      spec = JSON.parse(content);
    } catch {
      parsed = false;
    }
    if (!parsed) {
      return [
        heading,
        markdownCell(
          "This Vega-Lite node's specification is not valid JSON, so it is " +
            "recorded here rather than rendered:\n\n```json\n" +
            content +
            "\n```"
        ),
      ];
    }
    // A spec that reads `inputs` stacks every input's rows, each under the
    // scenario box its node is in, as the canvas does: by the same Python the
    // browser's twin is pinned to (utk_curio.sandbox.util.scenario_stack). It
    // draws the boxes' colors and order where its chips write them, as there.
    const stacked = readsInputs(spec);
    // json.loads of a JSON *string* would yield a Python str rather than a
    // dict, which is not a usable mimebundle - so the spec is embedded as a
    // literal object instead, written as Python writes one: JSON's true,
    // false and null are names a kernel does not know.
    const lines = [
      "from IPython.display import display",
      ...(stacked ? ["from utk_curio.sandbox.util.scenario_stack import input_rows, stack_inputs"] : []),
      "",
      `_spec = ${pythonLiteral(spec)}`,
    ];
    if (inputNodes.length && !usesNamedDatasets(spec, inputNodes.length)) {
      lines.push(
        "",
        "# Attach the upstream rows the canvas would have supplied.",
        `_spec["data"] = {"values": ${outputVarName(inputNodes[0])}.to_dict(orient="records")}`
      );
    } else if (inputNodes.length) {
      // Several inputs, or a spec naming one: each is the dataset input_<k>,
      // in circle order, as on the canvas (utils/vegaDatasets).
      lines.push("", "# Attach the upstream rows the canvas would have supplied, one dataset per input.");
      lines.push('_spec.setdefault("datasets", {})');
      inputNodes.forEach((input, position) => {
        // A spec that stacks its inputs takes values too, read as rows the way
        // the stacking reads them.
        const rows = stacked ? `input_rows(${outputVarName(input)})` : `${outputVarName(input)}.to_dict(orient="records")`;
        lines.push(`_spec["datasets"]["${inputTableName(position)}"] = ${rows}`);
      });
      if (stacked) {
        lines.push(
          "# Every input's rows stacked in circle order, each under the id and the name of its scenario's box.",
          `_spec["datasets"]["${INPUTS_DATASET}"] = stack_inputs([`,
          ...inputNodes.map((input, position) => {
            const label = inputLabels.get(inputSlots[position]) ?? { name: inputTableName(position), color: NO_SCENARIO_COLOR };
            const scenario = widgetLiteral(label.scenario ?? null, "python");
            return `    (${scenario}, ${widgetLiteral(label.name, "python")}, ${outputVarName(input)}),`;
          }),
          "])",
        );
      }
      if (!namesDataset((spec as any)?.data)) lines.push(`_spec["data"] = {"name": "${inputTableName(0)}"}`);
    }
    lines.push(
      "",
      'display({"application/vnd.vegalite.v5+json": _spec, "text/plain": "<VegaLite>"}, raw=True)'
    );
    return [heading, codeCell(lines.join("\n"))];
  }

  // Everything a Python kernel cannot run: an Autark grammar document (which
  // goes through the JS interpreter despite its manifest engine), a JavaScript
  // computation, and the presentational templates that carry no content.
  // Recorded, never silently dropped.
  const why =
    nodeType === NodeType.AUTK_GRAMMAR
      ? "An Autark grammar specification. It renders through Curio's WebGPU pipeline, not a Python kernel."
      : nodeType === NodeType.JS_COMPUTATION
        ? "A JavaScript computation. Its source is preserved below; it cannot run in this notebook's Python kernel."
        : nodeType === SPATIAL_JOIN_TYPE
          ? "A spatial join, configured on the canvas rather than in code."
          : nodeType === PARAMETER_NODE_TYPE
            ? "A Parameter node. Its value is written into the cells of the nodes that use it."
            : nodeType === NodeType.VIS_SIMPLE
              ? "A Simple View node, which displays its input rather than computing anything."
              : "This node is provided by a package and has no Python equivalent here.";
  const fenced = content.trim()
    ? `\n\n\`\`\`\n${content}\n\`\`\``
    : "";
  return [markdownCell(`${nodeHeading(node, inputNodes)}\n\n${why}${fenced}`)];
}

/** A TRILL node in the shape the canvas gives a node's data, which names it. */
function canvasNodeOf(node: TrillNode): { id: string; type: string; data: Record<string, unknown> } {
  return {
    id: node.id,
    type: node.type,
    data: { title: node.title, nodeType: node.type, packageTemplateLabel: node.metadata?.packageTemplateLabel },
  };
}

export function trillToNotebook(spec: TrillSpec): Notebook {
  const nodes = spec.dataflow?.nodes ?? [];
  const edges = spec.dataflow?.edges ?? [];

  // A node's inputs in circle order: input_0, input_1, ... as the canvas names them.
  const inputsOf = new Map<string, { source: string; slot: number }[]>(nodes.map((n) => [n.id, []]));
  for (const edge of edges) {
    if (edge.type !== "Interaction") {
      inputsOf.get(edge.target)?.push({ source: edge.source, slot: edgeSlot(edge) });
    }
  }
  inputsOf.forEach((list) => list.sort((a, b) => a.slot - b.slot));

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const ordered = dataflowOrder(nodes, edges.filter((edge) => edge.type !== "Interaction"));
  const shared = sharedWidgetsOfSpec(nodes);
  // Each node and edge as the canvas holds it, and the boxes as the canvas
  // reads them, so an input is labelled here as the canvas labels it.
  const canvasNodes = nodes.map(canvasNodeOf);
  const canvasEdges = edges
    .filter((edge) => edge.type !== "Interaction")
    .map((edge) => ({ source: edge.source, target: edge.target, targetHandle: slotHandleId(edgeSlot(edge)) }));
  const scenarios = normalizeScenarios(spec.dataflow?.scenarios, nodes.map((n) => n.id));

  const cells: NotebookCell[] = [];
  for (const node of ordered) {
    const wired = inputsOf.get(node.id) ?? [];
    const inputNodes = wired
      .map((input) => nodeById.get(input.source))
      .filter((n): n is TrillNode => n !== undefined);
    const labels = new Map(
      inputScenarios(node.id, canvasNodes, canvasEdges, scenarios).map((input) => [input.slot, input.label]),
    );
    cells.push(...generateCells(node, inputNodes, wired.map((input) => input.slot), shared, labels));
  }

  return {
    cells,
    metadata: {
      kernelspec: {
        display_name: "Python 3",
        language: "python",
        name: "python3",
      },
      language_info: { name: "python" },
    },
    nbformat: 4,
    nbformat_minor: 4,
  };
}

export function serializeNotebook(notebook: Notebook): string {
  return JSON.stringify(notebook, null, 2);
}
